import React from 'react';
import { Link } from 'react-router-dom';
import { BUSINESS, businessAddressLine } from '../constants/business';
import { contactEmail } from '../utils/email';

// Covers this website and the client area. The RiddleMe app has its own policy
// at /riddleme/privacypolicy — it's a different product with a different data
// model (ad identifiers, no accounts), so keeping them separate is clearer than
// one document trying to describe both.
const wrap = { padding: '20px', fontFamily: 'Arial, sans-serif', lineHeight: '1.6', maxWidth: '800px', margin: 'auto' };

const PrivacyPolicy = () => (
  <div style={wrap}>
    <h1 style={{ textAlign: 'center' }}>Privacy Policy</h1>
    <p style={{ fontStyle: 'italic', textAlign: 'center' }}>Last updated: 12 September 2026</p>

    <p>
      This policy explains what personal information {BUSINESS.name} collects through this website
      and our client area, why we collect it, and what rights you have over it.
    </p>
    <p>
      Our RiddleMe app is covered by a separate policy, which you can read{' '}
      <Link to="/riddleme/privacypolicy">here</Link>.
    </p>

    <h2>1. Who we are</h2>
    <p>
      {BUSINESS.name} (company number {BUSINESS.companyNumber}), {businessAddressLine()}, is the data
      controller for the information described in this policy. We are registered with the
      Information Commissioner's Office under registration number{' '}
      <a
        href={`https://ico.org.uk/ESDWebPages/Entry/${BUSINESS.icoNumber}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {BUSINESS.icoNumber}
      </a>, which you can check on the ICO's public register.
    </p>
    <p>You can contact us about anything in this policy at <a href={`mailto:${contactEmail()}`}>{contactEmail()}</a>.</p>

    <h2>2. What we collect</h2>

    <h3>When you send us an enquiry</h3>
    <p>Our contact form asks for your name, email address, and optionally your company, phone number, what kind of project you have in mind, a description of it, any questions, and how you would prefer we get in touch.</p>

    <h3>If you have a client account</h3>
    <p>Clients we work with get an account in our client area. For those accounts we hold:</p>
    <ul>
      <li>Your email address and your name or business name.</li>
      <li>A password, stored by Google in hashed form. We never see it and cannot recover it.</li>
      <li>Quotes we have sent you — what the work covers, prices, payment terms, and any contract wording attached.</li>
      <li>If you accept a quote, the name you typed to sign it and the date and time you accepted.</li>
      <li>Invoices we have raised, their amounts, dates and whether they have been paid.</li>
      <li>Any messages exchanged between us in the client area.</li>
    </ul>

    <h3>Notes we keep</h3>
    <p>
      We sometimes record short internal notes about a client — practical things such as who to
      contact about invoices, or how you prefer to work. These notes are not visible to you in the
      client area, but they are your personal information and you can ask us for a copy at any time.
    </p>

    <h3>What we don't do</h3>
    <p>
      We do not use analytics, advertising or tracking services on this website. We do not build
      profiles of visitors, and we never sell personal information or share it for marketing.
    </p>

    <h2>3. Why we use it, and our lawful basis</h2>
    <ul>
      <li><strong>Responding to enquiries.</strong> Our legitimate interest in answering people who contact us about our services.</li>
      <li><strong>Running your client account, quotes and invoices.</strong> Performance of our contract with you, or taking steps at your request before entering into one.</li>
      <li><strong>Keeping records of accepted quotes and issued invoices.</strong> Our legal obligation to keep accounting records, and our legitimate interest in being able to evidence what was agreed.</li>
      <li><strong>Internal notes.</strong> Our legitimate interest in delivering work well and keeping track of practical details.</li>
    </ul>

    <h2>4. Who else is involved</h2>
    <p>
      We use Google's Firebase platform to host this website and store the information above. Google
      acts as our processor, handling data on our instructions under its data processing terms. We do
      not share your information with anyone else unless we are required to by law.
    </p>

    <h2>5. Where your information is stored</h2>
    <p>
      Our Firebase database is hosted in Google's North America multi-region, which means your
      information is stored on servers in the United States. Transfers are covered by the UK
      extension to the EU–US Data Privacy Framework, under which Google is certified, together with
      the standard contractual clauses in Google's data processing terms.
    </p>

    <h2>6. How long we keep it</h2>
    <p>
      We keep personal information for as long as we need it, and no longer. While you are a client
      that means for the whole time we are working together, however many years that is — we need
      your account, quotes, invoices and messages to do the work and to answer questions about it
      later. The periods below start once that relationship ends.
    </p>
    <ul>
      <li><strong>Enquiries</strong> that do not lead to work: typically around two years, in case you come back to us.</li>
      <li><strong>Quotes</strong> that were never accepted: typically around two years.</li>
      <li><strong>Messages and internal notes:</strong> typically around two years after you stop being a client.</li>
      <li><strong>Accepted quotes, signatures and invoices:</strong> at least six years after the end of the financial year they relate to. This one is fixed, because we are required to keep accounting records for that long.</li>
      <li><strong>Your account:</strong> for as long as you want it. Ask us and we will close it.</li>
    </ul>
    <p>We review what we are holding periodically and delete anything we no longer have a reason to keep.</p>

    <h2>7. Keeping it secure</h2>
    <p>
      The client area is behind a login, and our database rules mean an account can only ever read
      its own quotes, invoices and messages — not anyone else's. Passwords are hashed by Google and
      never visible to us. Once you accept a quote, neither you nor we can alter what was agreed;
      the record is fixed. Internal notes are stored separately from your account record so they are
      never visible to you or to any other client.
    </p>
    <p>
      No system is perfect. If something does go wrong and your information is at risk, we will tell
      you and the Information Commissioner's Office as required.
    </p>

    <h2>8. Automated decisions</h2>
    <p>
      We do not make automated decisions about you, and we do not profile you. Quotes and prices are
      put together by a person.
    </p>

    <h2>9. Your rights</h2>
    <p>Under UK data protection law you can ask us to:</p>
    <ul>
      <li>Give you a copy of the personal information we hold about you, including our internal notes.</li>
      <li>Correct anything that is wrong.</li>
      <li>Delete information, where we do not have to keep it for accounting or legal reasons.</li>
      <li>Restrict or object to how we use it, including anything we do on the basis of legitimate interests.</li>
      <li>Provide your information in a portable format.</li>
    </ul>
    <p>
      Email us at <a href={`mailto:${contactEmail()}`}>{contactEmail()}</a> and we will respond within
      one month. If you are unhappy with how we have handled your information you can complain to the
      Information Commissioner's Office at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>,
      though we would appreciate the chance to put things right first.
    </p>

    <h2>10. Cookies and browser storage</h2>
    <p>
      We do not use advertising or analytics cookies, and there is no cookie banner because there is
      nothing to consent to. Two things are stored in your own browser:
    </p>
    <ul>
      <li>If you sign in to the client area, Google Firebase stores a sign-in token so you stay logged in.</li>
      <li>If you start filling in our contact form, your answers are saved in your browser so you don't lose them if the page reloads. They stay on your device and are only sent to us when you submit the form.</li>
    </ul>
    <p>Both are needed for the site to work as you'd expect, and neither is used to track you. Clearing your browser data removes them.</p>

    <h2>11. Children</h2>
    <p>This website and client area are for businesses, and are not intended for children.</p>

    <h2>12. Changes to this policy</h2>
    <p>
      If we change how we handle personal information we will update this page and the date at the
      top. If the change is significant and affects current clients, we will tell you directly.
    </p>

    <p style={{ marginTop: '32px' }}>
      Questions about any of this? Email <a href={`mailto:${contactEmail()}`}>{contactEmail()}</a>.
    </p>
  </div>
);

export default PrivacyPolicy;
